import { Permission } from '../api/roles.dao';

interface ISinglePermissionRequestUrl {
    url: string;
    permission: Permission;
}

interface IAnyPermissionRequestUrl {
    url: string;
    permissions: Permission[];
}

type IPermissionRequestUrl = ISinglePermissionRequestUrl | IAnyPermissionRequestUrl;

type IRequestUrl = string | IPermissionRequestUrl;

interface IResponseBase {
    response?: Response;
    rawResponse?: string | Blob;
}

interface IApiResponse<T> extends IResponseBase {
    parsedResponse?: T;
    rawResponse?: string;
}

interface IFileResponse extends IResponseBase {
    rawResponse?: Blob;
}

enum RequestContentType {
    formData = 'formData',
    zip = 'application/zip',
    xZip = 'multipart/x-zip',
    xZipCompressed = 'application/x-zip-compressed',
    json = 'json',
    imgPng = 'image/png',
    imgGif = 'image/gif',
    imgJpeg = 'image/jpeg',
    imgJpg = 'image/jpg',
    imgSvg = 'image/svg+xml',
    imgTiff = 'image/tiff',
    html = 'text/html',
    csv = 'text/csv',
    xls = 'application/vnd.ms-excel',
    xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    doc = 'application/msword',
    docx = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ppt = 'application/vnd.ms-powerpoint',
    pptx = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    txt = 'text/plain',
    pdf = 'application/pdf',
    ics = 'text/calendar',
    msg = 'application/vnd.ms-outlook',
    mp3 = 'audio/mp3',
    mpeg = 'audio/mpeg',
    wav = 'audio/wav',
    xWav = 'audio/x-wav',
    m4a = 'audio/mp4',
    xM4a = 'audio/x-m4a',
    audioWebm = 'audio/webm',
    ogg = 'audio/ogg',
    flac = 'audio/flac',
    xFlac = 'audio/x-flac',
    aac = 'audio/aac',
    mp4 = 'video/mp4',
    mov = 'video/mov',
    quicktime = 'video/quicktime',
    ttf = 'font/ttf',
    otf = 'font/otf',
    woff = 'application/font-woff',
    woff2 = 'font/woff2',
    eot = 'application/vnd.ms-fontobject'
}

enum HTTP_STATUS_CODE {
    CONFLICT = 409
}

interface IBaseRequestOptions {
    requestInit?: RequestInit;
    // parameters, which shall be passed as URI parameters ignoring request type
    uriParams?: {[key in string]: string};
    ContentType?: RequestContentType ;
    maxRetryAttempts?: number;
    // When true, bypass the automatic 5xx retry wrapper — use for non-idempotent calls (e.g. order create).
    disableRetry?: boolean;
    signal?: AbortSignal;
}

/**
 * describe options of requests, which are parsing the data into plain text with respect to content-type encoding
 */
interface IRequestOptions<T> extends IBaseRequestOptions {
    responseParser?: ResponseParser<T>;
}

/**
 * describe options of requests, which are not parsing retrieved data and either download it to browser or return blob
 */
interface IFileRequestOptions extends IBaseRequestOptions {
    /**
     * by default download file. If file shall not be downloaded by browser - set this option to 'false'
     */
    download?: boolean;
    /**
     * provide the way to override file name, received in content-disposition header
     */
    fileName?: string;
}

type ResponseParser<T> = (response: Response, responseBody: string) => T;

abstract class IApiClient {
    public abstract get<TResponse, TRequest = {}>(
        requestUrl: IRequestUrl,
        parameters?: TRequest,
        options?: IRequestOptions<TResponse>
    ): Promise<IApiResponse<TResponse>>;
    public abstract post<TResponse, TRequest = {}>(
        requestUrl: IRequestUrl,
        payload?: TRequest, options?: IRequestOptions<TResponse>
    ): Promise<IApiResponse<TResponse>>;
    public abstract put<TResponse, TRequest = {}>(
        requestUrl: IRequestUrl,
        payload?: TRequest,
        options?: IRequestOptions<TResponse>
    ): Promise<IApiResponse<TResponse>>;
    public abstract delete<TResponse, TRequest = {}>(
        requestUrl: IRequestUrl,
        parameters?: TRequest,
        options?: IRequestOptions<TResponse>
    ): Promise<IApiResponse<TResponse>>;
    public abstract getFile<TRequest = {}>(
        requestUrl: IRequestUrl,
        parameters?: TRequest,
        options?: IFileRequestOptions
    ): Promise<IFileResponse>;
    public abstract patch<TResponse, TRequest = {}>(
        requestUrl: IRequestUrl,
        payload?: TRequest,
        options?: IRequestOptions<TResponse>
    ): Promise<IApiResponse<TResponse>>;
}

const serviceAuthHeaderName = 'X-API-Authentication';

export {
    IApiClient,
    IApiResponse,
    ResponseParser,
    IRequestOptions,
    RequestContentType,
    IFileResponse,
    IResponseBase,
    IFileRequestOptions,
    IBaseRequestOptions,
    IPermissionRequestUrl,
    IRequestUrl,
    serviceAuthHeaderName,
    HTTP_STATUS_CODE
};
